/* @flow */

import * as React from 'react';
import type {Dispatch} from 'redux';
import {JoyrideIntroductionIcon, JoyrideReportsIcon} from 'shells/icon';

import {store, type NutshellState} from '../../store';

import {CHART_HERO_DIV_ID} from '../reports/report-constants';
import {persistNewJoyrideStepIndex} from './onboarding-helpers';
import * as actions from './onboarding-actions';
import * as selectors from './onboarding-selectors';

export const FALLBACK_STEP = {
    isResumable: undefined,
    onStepComplete: undefined,
    uri: undefined,
    selector: 'fallback',
};

export type Step = {
    selector: string,
    onStepComplete?: () => void,
    isResumable?: boolean,
    uri?: string,
};

export type DemoJoyrideId = 'NOUNS' | 'REPORTS';
export type JoyrideId =
    | DemoJoyrideId
    | 'ENTITY_LIST_LAYOUT_INTRO'
    | 'VISUAL_PIPELINE_INTRO'
    | 'ENTITY_LIST_PLUS_VP_INTRO'
    | 'EMAIL_DRIP_SEQUENCE_TEMPLATES'
    | 'CONSTANT_CONTACT_EXPORT';

/*
 * Joyride Names
 */
export const NOUNS = 'NOUNS';
export const REPORTS = 'REPORTS';
export const ENTITY_LIST_LAYOUT_INTRO = 'ENTITY_LIST_LAYOUT_INTRO';
export const VISUAL_PIPELINE_INTRO = 'VISUAL_PIPELINE_INTRO';
export const ENTITY_LIST_PLUS_VP_INTRO = 'ENTITY_LIST_PLUS_VP_INTRO';
export const EMAIL_DRIP_SEQUENCE_TEMPLATES = 'EMAIL_DRIP_SEQUENCE_TEMPLATES';
export const CONSTANT_CONTACT_EXPORT = 'CONSTANT_CONTACT_EXPORT';

const SCROLL_OFFSET_PIVOT_CHART = 70;
const SCROLL_OFFSET_HERO_CHART = 500;
const SCROLL_OFFSET_TITLE_BAR = 50;
//
// const HOLE_PADDING_FUNNEL_SECTION = 10;

export const PERSON_PLACEHOLDER = '$person-id';
export const LEAD_PLACEHOLDER = '$lead-id';
const ACME_CORP_COMPANY_ID = '1-acme-corp';

const demoJoyrideConstants = {
    [NOUNS]: {
        name: 'Introducing Nutshell',
        sectionId: 'introduction',
        iconComponent: JoyrideIntroductionIcon,
    },
    [REPORTS]: {
        name: 'View Reports',
        sectionId: 'reports',
        iconComponent: JoyrideReportsIcon,
    },
};

/*
 * InfoHeader Text
 */

export const DASHBOARD_INFOHEADER_TEXT =
    'This is an example dashboard. Exit the demo to customize your own personal dashboard.';
export const ENTITY_FORM_INFOHEADER_TEXT = 'This will only be saved inside the interactive demo.';

/*
 * Joyride Steps
 */
export const joyrideSteps = {
    [NOUNS]: [
        {
            text:
                'Here’s an example of how Nutshell can help you better manage your relationships.<br/><br/>This morning you met a prospect named <em>Bob Smith</em>. Go ahead and add Bob to Nutshell.',
            selector: '.nut-sidebar--create-button',
            position: 'right',
            isFixed: true,
            allowClicksThruHole: true,
            onTargetClick: (target: HTMLElement, onTargetClick: Function) =>
                advanceAndAllowAnimation(target, onTargetClick, 400),
            style: {
                button: {display: 'none'},
                back: {display: 'none'},
                hole: {backgroundColor: 'rgba(255, 255, 255, 0.5)'},
            },
        },
        {
            hideTooltip: true,
            selector: '#create-new-person',
            isFixed: true,
            isResumable: false,
            allowClicksThruHole: true,
            holePadding: 0,
            scrollToStep: false,
            onTargetClick: (target: HTMLElement, onTargetClick: Function) =>
                hackyPopulateFormAndFireEvent(target, onTargetClick, 'hackyAddPersonValues'),
        },
        {
            text:
                'First you’ll enter everything you know about Bob Smith, including the company where he works, Acme Corp.<br/>(For the purposes of this demo, we’ve gone ahead and done that.)',
            selector: '#field-accounts',
            position: 'right',
            isFixed: true,
            isResumable: false,
            allowClicksInHole: false,
            onCloseCallback: closeEntityModal,
            showOverlay: false,
            onStepCompleteCallback: enableFormFooterPointerEvents,
            style: {
                back: {display: 'none'},
                hole: {boxShadow: 'initial'},
            },
        },
        {
            text:
                'By clicking <em>Save</em>, you’ll be keeping all of Bob’s information in one place.',
            selector: 'button#save-entity',
            position: 'top',
            isFixed: true,
            isResumable: false,
            onCloseCallback: closeEntityModal,
            allowClicksThruHole: true,
            holePadding: 2,
            onTargetClick: (target: HTMLElement, onTargetClick: Function) =>
                persistJoyrideStepBeforeAdvancingCallback(target, onTargetClick, true),
            style: {button: {display: 'none'}, back: {display: 'none'}},
        },
        {
            text:
                'Nutshell has now created a <em>People</em> page for Bob Smith.<br/><br/>Here you can stay on top of all your interactions and deals with Bob.',
            selector: '#joyride-nav-introduction',
            uri: `/person/${PERSON_PLACEHOLDER}`,
            position: 'top',
            isFixed: true,
            scrollOffset: 200,
            style: {
                back: {display: 'none'},
                arrow: {display: 'none'},
                hole: {boxShadow: 'none'},
            },
        },
        {
            text:
                'To minimize data entry, every time you add a contact, Nutshell scours the internet to fill in details such as Bob’s photo, job title, social media accounts, address, and more.',
            selector: '#joyride-sidebar-blocks',
            uri: `/person/${PERSON_PLACEHOLDER}`,
            position: 'left',
            showOverlay: true,
            allowClicksThruHole: false,
            scrollOffset: 200,
        },
        {
            text:
                'The company where Bob works, Acme Corp, is already an ArrowGrid customer. Jump to Acme Corp’s Company page.',
            selector: '.tile-account-top',
            uri: `/person/${PERSON_PLACEHOLDER}`,
            position: 'top',
            showOverlay: true,
            allowClicksThruHole: true,
            scrollOffset: 200,
            onTargetClick: persistJoyrideStepBeforeAdvancingCallback,
            onStepNextCallback: manuallyPersistAndRedirectJoyride,
        },
        {
            text:
                'From the <em>Company</em> page, you can get information about the whole account.',
            selector: '#joyride-nav-introduction',
            uri: `/company/${ACME_CORP_COMPANY_ID}`,
            position: 'top',
            isFixed: true,
            scrollOffset: 50,
            style: {
                arrow: {display: 'none'},
                hole: {boxShadow: 'none'},
            },
        },
        {
            text: 'You can see a list of all the People your team knows at Acme Corp.',
            selector: '.contacts-list',
            uri: `/company/${ACME_CORP_COMPANY_ID}`,
            position: 'top',
            // There's a bug in the Joyride that looks at the previous step's scrollOffset
            // and it uses that in the next step's calculation - we need this scrollOffset
            // here because of that.
            scrollOffset: 50,
        },
        {
            text: 'At a glance, you can see your open deals and purchase history with Acme Corp.',
            selector: '#for-joyride-peep-related-leads',
            uri: `/company/${ACME_CORP_COMPANY_ID}`,
            position: 'left',
            holePadding: 8,
            scrollOffset: 10,
        },
        {
            text:
                'In your conversation with Bob, you uncovered a new sales opportunity. In Nutshell, this is called a <em>Lead</em>.<br/><br/>Create a new Lead.',
            selector: '#for-joyride-create-lead-from-peep button',
            uri: `/company/${ACME_CORP_COMPANY_ID}`,
            position: 'left',
            holePadding: 5,
            scrollOffset: 70,
            allowClicksThruHole: true,
            onStepNextCallback: manuallyClickSelector,
            onTargetClick: (target: HTMLElement, onTargetClick: Function) =>
                hackyPopulateFormAndFireEvent(target, onTargetClick, 'hackyAddLeadValues'),
        },
        {
            text: 'Enter details about the Lead to update your sales pipeline.',
            selector: '#field-productMaps',
            position: 'right',
            showOverlay: false,
            isFixed: true,
            isResumable: false,
            onCloseCallback: closeEntityModal,
            onStepCompleteCallback: enableFormFooterPointerEvents,
            style: {
                back: {display: 'none'},
                hole: {boxShadow: 'initial'},
            },
        },
        {
            text:
                'Save the Lead to automatically add it to your sales process so you won’t forget to follow up.',
            selector: 'button#save-entity',
            position: 'top',
            isFixed: true,
            isResumable: false,
            onCloseCallback: closeEntityModal,
            allowClicksThruHole: true,
            onTargetClick: (target: HTMLElement, onTargetClick: Function) =>
                persistJoyrideStepBeforeAdvancingCallback(target, onTargetClick, true),
            holePadding: 2,
            style: {button: {display: 'none'}, back: {display: 'none'}},
        },
        {
            text:
                'This Lead page is where you will track everything about this potential deal—from your first conversations through the win.',
            selector: '#joyride-nav-introduction',
            uri: `/lead/${LEAD_PLACEHOLDER}`,
            position: 'top',
            isFixed: true,
            scrollOffset: 50,
            style: {
                back: {display: 'none'},
                arrow: {display: 'none'},
                hole: {boxShadow: 'none'},
            },
        },
        {
            text:
                'These stages have been customized for our business, to match how we win a deal. Since we already had a first conversation, let’s move this lead to the next stage. Click on "Qualify" above.',
            selector:
                '.ui-stages-carousel__scroll-container .lead-stage-chevron-for-joyride:nth-child(2) > div',
            uri: `/lead/${LEAD_PLACEHOLDER}`,
            position: 'bottom',
            allowClicksThruHole: true,
            onTargetClick: (target: HTMLElement, onTargetClick: Function) =>
                advanceAndAllowAnimation(target, onTargetClick, 2000),
            isFixed: false,
            scrollOffset: 100,
            holePadding: 0,
            style: {
                hole: {boxShadow: 'none'},
                button: {display: 'none'},
                back: {display: 'none'},
            },
        },
        {
            text:
                'Great work. This timeline automatically includes every email, phone call, and internal note about the lead.',
            selector: '.lead-timeline-container',
            uri: `/lead/${LEAD_PLACEHOLDER}`,
            position: 'top',
            showOverlay: true,
            onStepNextCallback: receiveFakeEmail,
            holePadding: 0,
        },
        {
            selector: '#lead-timeline',
            uri: `/lead/${LEAD_PLACEHOLDER}`,
            position: 'top',
            showOverlay: true,
            hideTooltip: true,
            isResumable: false,
            holePadding: 0,
        },
        {
            text: 'Whoa! Looks like Bob just emailed us and wants to close the deal!',
            selector: '.timeline3-entry.type-emails',
            uri: `/lead/${LEAD_PLACEHOLDER}`,
            position: 'top',
            showOverlay: true,
            onStepPreviousCallback: moveBackTwoSteps,
        },
        {
            text: 'Let’s win this lead.',
            selector: '.lead-status__menu #react-select-2-option-1',
            uri: `/lead/${LEAD_PLACEHOLDER}`,
            position: 'top',
            showOverlay: true,
            isResumable: false,
            allowClicksThruHole: true,
            holePadding: 0,
            scrollOffset: 200,
            onTargetClick: persistJoyrideStepBeforeAdvancingCallback,
            style: {button: {display: 'none'}, back: {display: 'none'}},
        },
        {
            text: 'You completed the tour!',
            selector: '#joyride-nav-introduction',
            uri: `/lead/${LEAD_PLACEHOLDER}`,
            position: 'top',
            isFixed: true,
            showOverlay: false,
            style: {back: {display: 'none'}, close: {display: 'none'}},
        },
    ],
    [REPORTS]: [
        {
            text: 'This tour will walk through Nutshell’s powerful reporting features.',
            selector: '#joyride-nav-reports',
            isFixed: true,
            showOverlay: false,
            style: {back: {display: 'none'}},
        },
        {
            text:
                'This is Nutshell’s sales report. From here, we can quickly track revenue over time, and learn about how they’re influenced',
            selector: '#joyride-nav-reports',
            uri: '/report/sales',
            holePadding: 0,
            isFixed: true,
            showOverlay: false,
            position: 'top',
            style: {back: {display: 'none'}},
        },
        {
            text:
                'Several months ago, we launched a new service-based offering, and here we can see the uptick in sales.',
            selector: `#${CHART_HERO_DIV_ID}`,
            uri: '/report/sales',
            position: 'bottom',
            holePadding: 0,
            style: {back: {display: 'none'}},
            scrollOffset: SCROLL_OFFSET_HERO_CHART,
        },
        {
            text:
                'Over the past year, we can see that Jason Richards was our top seller, and that Steve Kennedy is struggling to keep up',
            selector: '#chart-assignee',
            uri: '/report/sales',
            position: 'top',
            holePadding: 0,
            scrollOffset: SCROLL_OFFSET_PIVOT_CHART,
        },
        {
            text:
                'Our new Commercial Installation service is now responsible for 30% of this year’s revenue',
            selector: '#chart-source',
            uri: '/report/sales',
            position: 'top',
            holePadding: 0,
            scrollOffset: SCROLL_OFFSET_PIVOT_CHART,
        },
        {
            text:
                'Now we’re looking at the new leads report, to check out how we’re doing on the prospecting front.',
            selector: '#report-mount',
            uri: '/report/newleads',
            position: 'bottom',
            holePadding: 0,
            scrollOffset: SCROLL_OFFSET_TITLE_BAR,
        },
        {
            text:
                'We can see a uptick in new leads over the past few weeks — we’ll want to dig into this to understand what’s happening.',
            selector: `#${CHART_HERO_DIV_ID}`,
            uri: '/report/newleads',
            position: 'bottom',
            holePadding: 0,
            scrollOffset: SCROLL_OFFSET_HERO_CHART,
        },
        {
            text:
                'Here’s where our new leads are coming from. Last month’s trade show is really paying off for us!',
            selector: '#chart-source',
            uri: '/report/newleads',
            position: 'top',
            holePadding: 0,
            scrollOffset: SCROLL_OFFSET_PIVOT_CHART,
        },
        {
            text:
                'This is Nutshell’s funnel report, which visualizes and evaluates how leads progress through our custom sales process.',
            selector: '#report-mount',
            uri: '/report/funnel',
            position: 'bottom',
            holePadding: 0,
            scrollOffset: SCROLL_OFFSET_TITLE_BAR,
        },
        // {
        //     text: 'We’re seeing quite a few leads getting stuck and slowing down in the Proposal stage',
        //     selector: '#funnel-section-3',
        //     position: 'top',
        //     holePadding: HOLE_PADDING_FUNNEL_SECTION,
        // },
    ],
    [ENTITY_LIST_LAYOUT_INTRO]: [
        {
            title: 'What’s new',
            text: (
                <span>
                    To help your team maximize efficiency, your Leads, People, and Companies pages
                    now have the same filter drawer as your Reports.
                    <br />
                    <br />
                    Learn more about filtering changes in the{' '}
                    <a
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{textDecoration: 'underline'}}
                        href='https://support.nutshell.com/hc/en-us/articles/115012698967-Video-Filter-your-companies-and-people'
                    >
                        Help center
                    </a>
                    .
                </span>
            ),
            selector: '#filter-button',
            position: 'bottom',
            style: {hole: {boxShadow: 'none'}},
        },
        {
            text: 'To save you time, the most common filters can be easily accessed from here.',
            selector: '#graduated-filters',
            position: 'bottom',
            style: {hole: {boxShadow: 'none'}},
        },
        {
            text: 'You can continue to access and manage your saved lists by clicking this button.',
            selector: '#saved-filters-btn',
            position: 'bottom',
            style: {hole: {boxShadow: 'none'}},
        },
    ],
    [VISUAL_PIPELINE_INTRO]: [
        {
            text: (
                <span>
                    Here’s where your new drag-and-drop Board lives. Visit this tab to see the
                    status of your team’s leads and easily move a lead through your sales process.
                    <br />
                    <br />
                    Learn more about the board in the{' '}
                    <a
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{textDecoration: 'underline'}}
                        href='https://support.nutshell.com/hc/en-us/articles/115012960128-How-do-I-use-the-Lead-Board-'
                    >
                        Help center
                    </a>
                    .
                </span>
            ),
            selector: 'label[for="board"]',
            position: 'bottom',
            style: {hole: {boxShadow: 'none'}},
        },
    ],
    [EMAIL_DRIP_SEQUENCE_TEMPLATES]: [
        {
            text: (
                <span>
                    Welcome to Nutshell’s email template composer. Here’s an idea to help you get
                    started - just update the content to make it your own!
                </span>
            ),
            selector: '#for-joyride-email-template-body',
            position: 'bottom',
            isFixed: true,
            style: {hole: {boxShadow: 'none'}},
        },
        {
            text: (
                <span>
                    Click here to add smart fields like &quot;First name&quot; and &quot;Last
                    name&quot; to your emails to save time.
                </span>
            ),
            selector: '#for-joyride-placeholder-select',
            position: 'top',
            isFixed: true,
            style: {hole: {boxShadow: 'none'}},
        },
        {
            text: (
                <span>
                    Now, our Pro customers can create email templates which include easy follow-up.
                    Each message here will be sent automatically if your recipient doesn’t reply
                    first.
                </span>
            ),
            selector: '#for-joyride-email-template-list',
            position: 'right',
            isFixed: true,
            style: {hole: {boxShadow: 'none'}},
        },
    ],
    [CONSTANT_CONTACT_EXPORT]: [
        {
            text: (
                <span>
                    <b>Send to Constant Contact</b>
                    <p>
                        Select Nutshell people, then click this button to send them to Constant
                        Contact!
                    </p>
                </span>
            ),
            selector: '#for-joyride-send-to-constant-contact',
            position: 'top',
            isFixed: false,
            style: {hole: {boxShadow: 'none'}},
        },
    ],
    [ENTITY_LIST_PLUS_VP_INTRO]: [],
};

joyrideSteps[ENTITY_LIST_PLUS_VP_INTRO] = [
    ...joyrideSteps.ENTITY_LIST_LAYOUT_INTRO,
    ...joyrideSteps.VISUAL_PIPELINE_INTRO,
];

function closeEntityModal() {
    const event = new CustomEvent('client-navigate', {
        detail: {
            type: 'replace',
            to: {
                hash: '',
                search: location.search,
            },
        },
    });
    document.dispatchEvent(event);
}

function advanceAndAllowAnimation(
    target: HTMLElement,
    targetOriginalOnClick: Function,
    animationDurationMs: number = 100
) {
    const state = getState();
    const activeJoyrideId = selectors.getActiveJoyrideId(state);
    const currentStepIndex = selectors.getActiveStepIndex(state);
    const newStepIndex = currentStepIndex + 1;
    const newStep = selectors.getJoyrideStep(state, activeJoyrideId, newStepIndex) || FALLBACK_STEP;
    store.dispatch(actions.temporarilyStopJoyride());

    targetOriginalOnClick();

    setTimeout(() => {
        store.dispatch(
            actions.changeJoyrideStepIndexById({
                joyrideId: activeJoyrideId,
                stepIndex: newStepIndex,
            })
        );
        store.dispatch(actions.startJoyrideById(activeJoyrideId));
    }, animationDurationMs);

    if (newStep.isResumable !== false) {
        persistNewJoyrideStepIndex(activeJoyrideId, newStepIndex);
    }
}

function hackyPopulateFormAndFireEvent(target, targetOriginalOnClick, eventName) {
    const state = getState();
    const activeJoyrideId = selectors.getActiveJoyrideId(state);
    const currentStepIndex = selectors.getActiveStepIndex(state);
    const newStepIndex = currentStepIndex + 1;
    const newStep = selectors.getJoyrideStep(state, activeJoyrideId, newStepIndex) || FALLBACK_STEP;

    store.dispatch(actions.temporarilyStopJoyride());

    targetOriginalOnClick();

    // Technically this is happening before we've received confirmation
    // from the server - I think this is OK, let's optimisically advance
    // the joyride and expect at 200. Even if we don't get it, not really
    // a big deal in this case.
    store.dispatch(actions.changeJoyrideStepIndexById({joyrideId: NOUNS, stepIndex: newStepIndex}));

    const populateAndRestart = () => {
        setTimeout(() => {
            disableFormPointerEvents();
            window.dispatchEvent(new CustomEvent(eventName));
        }, 300);
        setTimeout(() => {
            store.dispatch(actions.startJoyrideById(NOUNS));
        }, 1000);
    };

    if (newStep.isResumable !== false) {
        persistNewJoyrideStepIndex(NOUNS, newStepIndex).then(populateAndRestart);
    } else {
        populateAndRestart();
    }
}

function disableFormPointerEvents() {
    const formBody = document.querySelector('#entity-form-body');
    const footerBar = document.querySelector('#create-entity-footer');

    if (formBody) {
        formBody.style.pointerEvents = 'none';
    }

    if (footerBar) {
        footerBar.style.pointerEvents = 'none';
    }
}

function enableFormFooterPointerEvents() {
    const footerBar = document.querySelector('#create-entity-footer');

    if (footerBar) {
        footerBar.style.pointerEvents = 'auto';
    }
}

function persistJoyrideStepBeforeAdvancingCallback(
    target: HTMLElement,
    targetOriginalOnClick: Function,
    shouldStop: boolean = false
) {
    const state = getState();
    const activeJoyrideId = selectors.getActiveJoyrideId(state);
    const currentStepIndex = selectors.getActiveStepIndex(state);

    if (shouldStop) {
        store.dispatch(actions.temporarilyStopJoyride());
    }

    // Adding 1 to currentStepIndex because we persist the _new_ currentStepIndex
    persistNewJoyrideStepIndex(activeJoyrideId, currentStepIndex + 1).then(() => {
        targetOriginalOnClick();
    });
}

function manuallyPersistAndRedirectJoyride(
    dispatch: Dispatch<*>,
    step: Step,
    activeJoyrideId: JoyrideId,
    nextStepIndex: number
) {
    dispatch(actions.temporarilyStopJoyride());

    const el = document.querySelector(step.selector);
    if (el) {
        persistNewJoyrideStepIndex(activeJoyrideId, nextStepIndex).then(() => {
            el.click();
        });
    }
}

function manuallyClickSelector(dispatch: Dispatch<*>, step: Step) {
    dispatch(actions.temporarilyStopJoyride());

    const el = document.querySelector(step.selector);
    if (el) {
        el.click();
    }
}

function moveBackTwoSteps(
    dispatch: Dispatch<*>,
    step: Step,
    activeJoyrideId: JoyrideId,
    prevStepIndex: number
) {
    const twoStepsBackIndex = prevStepIndex - 1;

    dispatch(actions.temporarilyStopJoyride());
    dispatch(
        actions.changeJoyrideStepIndexById({
            joyrideId: activeJoyrideId,
            stepIndex: twoStepsBackIndex,
        })
    );
    dispatch(actions.startJoyrideById(activeJoyrideId));

    if (joyrideSteps[activeJoyrideId][twoStepsBackIndex].isResumable !== false) {
        persistNewJoyrideStepIndex(activeJoyrideId, twoStepsBackIndex);
    }
}

function receiveFakeEmail() {
    const postData = {
        id: getDynamicPersonIdWithDOMFallback(),
    };

    $.ajax({
        type: 'POST',
        url: '/rest/onboarding/receivefakeemail.json',
        dataType: 'json',
        contentType: 'application/json',
        data: JSON.stringify({data: postData}),
    }).then(() => {
        $(window).trigger(Timeline.events.TimelineRefresh);
        setTimeout(() => {
            const currentStepIndex = selectors.getActiveStepIndex(getState());
            persistNewJoyrideStepIndex(NOUNS, currentStepIndex + 2).then(() => {
                store.dispatch(
                    actions.changeJoyrideStepIndexById({
                        joyrideId: NOUNS,
                        stepIndex: currentStepIndex + 2,
                    })
                );
            });
        }, 200);
    });
}

function getDynamicPersonIdWithDOMFallback() {
    let personId = $('.contact-card-container.tile-filled').data('id');
    if (
        typeof NutClientConfig !== 'undefined' &&
        NutClientConfig.onboardingJoyrideCreatedPersonId
    ) {
        personId = NutClientConfig.onboardingJoyrideCreatedPersonId;
    }

    return personId;
}

export function getJoyrideName(joyrideId: DemoJoyrideId) {
    if (!joyrideId || !demoJoyrideConstants[joyrideId]) return undefined;

    return demoJoyrideConstants[joyrideId].name;
}

export function getJoyrideSectionId(joyrideId: DemoJoyrideId) {
    if (!joyrideId || !demoJoyrideConstants[joyrideId]) return undefined;

    return demoJoyrideConstants[joyrideId].sectionId;
}

export function getJoyrideIcon(joyrideId: DemoJoyrideId) {
    if (!joyrideId || !demoJoyrideConstants[joyrideId]) return undefined;

    return demoJoyrideConstants[joyrideId].iconComponent;
}

export function isJoyrideADemo(joyrideId: JoyrideId) {
    return Object.keys(demoJoyrideConstants).includes(joyrideId);
}

function getState(): NutshellState {
    // $FlowFixMe upgrading Flow to v0.92.1 on web
    return store.getState();
}
